import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Music, ArrowLeft, Upload, Plus, Trash2, 
  GripVertical, Copy, Check, ExternalLink,
  QrCode, Download, Search, Loader2, Sun, Moon, Settings,
  ChevronRight, ChevronDown, X, Pencil
} from "lucide-react";
import { FaSpotify, FaApple, FaYoutube, FaItunes, FaNapster } from "react-icons/fa";
import { SiTidal, SiPandora, SiAudiomack } from "react-icons/si";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { getMainDomainUrl } from "@/utils/subdomain";
import PagePreview from "@/components/PagePreview";
import MuslinkLogo from "@/components/MuslinkLogo";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Custom SVG icon components using uploaded SVG files
const SoundCloudIcon = (props) => (
  <img src="/icons/sc-mus.svg" alt="SoundCloud" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

const TikTokIcon = (props) => (
  <img src="/icons/tik-tok.svg" alt="TikTok" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

const VKMusicIcon = (props) => (
  <img src="/icons/vk-mus.svg" alt="VK Музыка" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

const YandexMusicIcon = (props) => (
  <img src="/icons/yandex-mus.svg" alt="Яндекс Музыка" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

const CustomLinkIcon = (props) => (
  <img src="/icons/link.svg" alt="Ссылка" {...props} style={{ width: props.width || '1.5em', height: props.height || '1.5em', ...props.style }} />
);

// YouTube Music icon using uploaded SVG
const YouTubeMusicIcon = (props) => (
  <img src="/icons/youtube-music.svg" alt="YouTube Music" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Amazon Music icon
const AmazonMusicIcon = (props) => (
  <img src="/icons/amazon-mus.svg" alt="Amazon Music" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Amazon Store icon
const AmazonStoreIcon = (props) => (
  <img src="/icons/amazon-store.svg" alt="Amazon Store" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Zvuk icon
const ZvukIcon = (props) => (
  <img src="/icons/zvuk-mus.svg" alt="Звук" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Anghami icon
const AnghamiIcon = (props) => (
  <img src="/icons/anghami-mus.png" alt="Anghami" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Audius icon
const AudiusIcon = (props) => (
  <img src="/icons/audius-mus.png" alt="Audius" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Bandcamp icon
const BandcampIcon = (props) => (
  <img src="/icons/bandcamp-mus.png" alt="Bandcamp" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// MTS Music icon
const MtsIcon = (props) => (
  <img src="/icons/mts-mus.png" alt="MTS Music" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Deezer icon
const DeezerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.01 11.75H0V15h6.01v-3.25zM6.01 7.25H0v3.25h6.01V7.25zM6.01 16.25H0v3.25h6.01v-3.25zM12.005 11.75H6.01V15h5.995v-3.25zM12.005 16.25H6.01v3.25h5.995v-3.25zM17.995 11.75H12V15h5.995v-3.25zM17.995 16.25H12v3.25h5.995v-3.25zM17.995 7.25H12v3.25h5.995V7.25zM24 11.75h-6.005V15H24v-3.25zM24 16.25h-6.005v3.25H24v-3.25zM24 7.25h-6.005v3.25H24V7.25zM24 2.75h-6.005V6H24V2.75z"/>
  </svg>
);

// Pandora icon
const PandoraIcon = (props) => (
  <img src="/icons/pandora-mus.png" alt="Pandora" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Boomplay icon
const BoomplayIcon = (props) => (
  <img src="/icons/boomplay-mus.png" alt="Boomplay" {...props} style={{ width: props.width || '1.8em', height: props.height || '1.8em', ...props.style }} />
);

// Platform order for auto-fill sorting (lower index = higher priority)
const PLATFORM_ORDER = [
  "spotify",       // 1. Spotify
  "appleMusic",    // 2. Apple Music
  "itunes",        // 3. iTunes
  "youtube",       // 4. YouTube
  "youtubeMusic",  // 5. YouTube Music
  "yandex",        // 6. Яндекс Музыка
  "vk",            // 7. VK Music
  "tiktok",        // 8. TikTok
  "deezer",        // 9. Deezer
  "tidal",         // 10. Tidal
  "amazonMusic",   // 11. Amazon Music
  "amazonStore",   // 12. Amazon Store
  "soundcloud",    // 13. SoundCloud
  "pandora",       // 13. Pandora
  "napster",       // 14. Napster
  "audiomack",     // 15. Audiomack
  "audius",        // 16. Audius
  "anghami",       // 17. Anghami
  "boomplay",      // 18. Boomplay
  "bandcamp",      // 19. Bandcamp
  "zvuk",          // 20. Звук
  "mts",           // 21. МТС Музыка
  "custom",        // 22. Custom
];

// Auto-detect platform from URL
const detectPlatformFromUrl = (url) => {
  if (!url) return null;
  const urlLower = url.toLowerCase();
  
  // Special case: Check for iTunes parameter in Apple Music URLs
  if (urlLower.includes("music.apple.com") && urlLower.includes("app=itunes")) {
    return "itunes";
  }
  
  const patterns = [
    { platform: "spotify", patterns: ["open.spotify.com", "spotify.com"] },
    { platform: "appleMusic", patterns: ["music.apple.com"] },
    { platform: "itunes", patterns: ["itunes.apple.com"] },
    { platform: "youtube", patterns: ["youtube.com/watch", "youtu.be"] },
    { platform: "youtubeMusic", patterns: ["music.youtube.com"] },
    // Yandex Music - support all regional domains (ru, kz, by, uz, etc.)
    { platform: "yandex", patterns: ["music.yandex."] },
    { platform: "vk", patterns: ["vk.com/music", "vk.com/audio", "boom.ru"] },
    { platform: "tiktok", patterns: ["tiktok.com", "vm.tiktok.com"] },
    { platform: "deezer", patterns: ["deezer.com", "deezer.page.link"] },
    { platform: "tidal", patterns: ["tidal.com", "listen.tidal.com"] },
    { platform: "amazonMusic", patterns: ["music.amazon", "amazon.com/music"] },
    { platform: "amazonStore", patterns: ["amazon.com/dp", "amazon.com/gp", "amzn.to"] },
    { platform: "soundcloud", patterns: ["soundcloud.com"] },
    { platform: "pandora", patterns: ["pandora.com"] },
    { platform: "napster", patterns: ["napster.com"] },
    { platform: "audiomack", patterns: ["audiomack.com"] },
    { platform: "audius", patterns: ["audius.co"] },
    { platform: "anghami", patterns: ["anghami.com"] },
    { platform: "boomplay", patterns: ["boomplay.com"] },
    { platform: "bandcamp", patterns: ["bandcamp.com"] },
    { platform: "zvuk", patterns: ["zvuk.com", "sber-zvuk.com"] },
    { platform: "mts", patterns: ["music.mts.ru"] },
  ];
  
  for (const { platform, patterns: urlPatterns } of patterns) {
    for (const pattern of urlPatterns) {
      if (urlLower.includes(pattern)) {
        return platform;
      }
    }
  }
  
  // If URL but no match, return custom
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return "custom";
  }
  
  return null;
};

// Supported autofill source patterns (URL hosts Odesli/our search can resolve)
const AUTOFILL_SOURCE_PATTERNS = [
  "music.apple.com", "itunes.apple.com",
  "open.spotify.com", "spotify.com",
  "youtube.com", "youtu.be", "music.youtube.com",
  "deezer.com", "deezer.page.link",
  "tidal.com", "listen.tidal.com",
  "soundcloud.com",
  "music.yandex.ru", "music.yandex.kz", "music.yandex.",
  "amazon.com/music", "music.amazon",
  "pandora.com",
  "napster.com",
  "audiomack.com",
  "audius.co",
  "anghami.com",
  "boomplay.com",
  "spinrilla.com",
  "bandcamp.com",
  "song.link", "album.link", "odesli.co",
];

// Returns true if the input is a valid autofill source (supported URL or UPC code)
const isAutofillSource = (value) => {
  if (!value) return false;
  const v = value.trim();
  if (/^\d{10,14}$/.test(v)) return true; // UPC
  return AUTOFILL_SOURCE_PATTERNS.some((p) => v.toLowerCase().includes(p));
};

// Platform definitions (without names - names come from translations)
const PLATFORMS = [
  { id: "spotify", icon: FaSpotify, color: "#1DB954" },
  { id: "appleMusic", icon: FaApple, color: "#FA233B" },
  { id: "itunes", icon: FaItunes, color: "#EA4CC0" },
  { id: "youtube", icon: FaYoutube, color: "#FF0000" },
  { id: "youtubeMusic", icon: YouTubeMusicIcon, color: "#FF0000", isImage: true },
  { id: "yandex", icon: YandexMusicIcon, color: "#000000", isImage: true },
  { id: "vk", icon: VKMusicIcon, color: "#000000", isImage: true },
  { id: "tiktok", icon: TikTokIcon, color: "#000000", isImage: true },
  { id: "deezer", icon: DeezerIcon, color: "#A238FF" },
  { id: "tidal", icon: SiTidal, color: "#000000" },
  { id: "amazonMusic", icon: AmazonMusicIcon, color: "#000000", isImage: true },
  { id: "amazonStore", icon: AmazonStoreIcon, color: "#FF9900", isImage: true },
  { id: "soundcloud", icon: SoundCloudIcon, color: "#000000", isImage: true },
  { id: "pandora", icon: PandoraIcon, color: "#000000", isImage: true },
  { id: "napster", icon: FaNapster, color: "#000000" },
  { id: "audiomack", icon: SiAudiomack, color: "#FFA200" },
  { id: "audius", icon: AudiusIcon, color: "#000000", isImage: true },
  { id: "anghami", icon: AnghamiIcon, color: "#000000", isImage: true },
  { id: "boomplay", icon: BoomplayIcon, color: "#000000", isImage: true },
  { id: "bandcamp", icon: BandcampIcon, color: "#000000", isImage: true },
  { id: "zvuk", icon: ZvukIcon, color: "#FFFFFF", isImage: true },
  { id: "mts", icon: MtsIcon, color: "#000000", isImage: true },
  { id: "custom", icon: CustomLinkIcon, color: "#000000", isImage: true },
];

// Sortable Link Item Component for Desktop
function SortableLinkItem({ link, platform, onToggle, onDelete, onUpdateUrl, t }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id });

  const [editing, setEditing] = useState(false);
  const [editUrl, setEditUrl] = useState(link.url || "");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const Icon = platform.icon;

  const startEdit = () => {
    setEditUrl(link.url || "");
    setEditing(true);
  };

  const saveEdit = () => {
    const v = (editUrl || "").trim();
    if (v && v !== link.url) {
      onUpdateUrl(link.id, v);
    }
    setEditing(false);
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: isDragging ? 0.4 : 1, y: 0 }}
      className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl panel-card w-full overflow-hidden group select-none ${
        isDragging ? "ring-2 ring-primary/70 bg-card" : ""
      }`}
      data-testid={`link-item-${link.id}`}
    >
      {/* Drag Handle - visible on hover */}
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 touch-none"
      >
        <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
      </div>
      <div 
        className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: platform.color }}
      >
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Input
              value={editUrl}
              autoFocus
              onChange={(e) => setEditUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                if (e.key === "Escape") setEditing(false);
              }}
              className="h-8 text-sm bg-background"
              placeholder="https://..."
            />
            <Button size="icon" variant="ghost" onClick={saveEdit} className="h-7 w-7 text-green-500 flex-shrink-0">
              <Check className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => setEditing(false)} className="h-7 w-7 text-muted-foreground flex-shrink-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEdit}
            title={t('pageBuilder', 'editLinkUrl')}
            className="text-left w-full group/edit"
          >
            <p className="font-medium text-sm sm:text-base flex items-center gap-1.5">
              {platform.name}
              <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover/edit:opacity-100 transition-opacity" />
            </p>
            {link.url && (
              <p className="text-xs text-muted-foreground truncate">{link.url}</p>
            )}
          </button>
        )}
      </div>
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        <Switch
          checked={link.active}
          onCheckedChange={() => onToggle(link.id, link.active)}
          data-testid={`toggle-link-${link.id}`}
        />
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => onDelete(link.id)}
          className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-red-400"
          data-testid={`delete-link-${link.id}`}
        >
          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </motion.div>
  );
}

// Sortable Link Item Component for Mobile
function MobileSortableLinkItem({ link, platform, onToggle, onDelete, onUpdateUrl, t }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id });

  const [editing, setEditing] = useState(false);
  const [editUrl, setEditUrl] = useState(link.url || "");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const Icon = platform.icon;

  const saveEdit = () => {
    const v = (editUrl || "").trim();
    if (v && v !== link.url) {
      onUpdateUrl(link.id, v);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-1.5 p-3 rounded-xl bg-card border border-primary/40"
      >
        <Input
          value={editUrl}
          autoFocus
          onChange={(e) => setEditUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveEdit();
            if (e.key === "Escape") setEditing(false);
          }}
          className="h-8 text-xs bg-background flex-1"
          placeholder="https://..."
        />
        <Button size="icon" variant="ghost" onClick={saveEdit} className="h-7 w-7 text-green-500 flex-shrink-0">
          <Check className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" onClick={() => setEditing(false)} className="h-7 w-7 text-muted-foreground flex-shrink-0">
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border select-none ${
        isDragging ? "ring-2 ring-primary/70 bg-card" : ""
      }`}
    >
      {/* Drag Handle - always visible on mobile */}
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      <div 
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: platform.color + '20' }}
      >
        {platform.isImage ? (
          <Icon className="w-5 h-5" />
        ) : (
          <Icon className="w-5 h-5" style={{ color: platform.color }} />
        )}
      </div>
      <button
        type="button"
        onClick={() => { setEditUrl(link.url || ""); setEditing(true); }}
        className="flex-1 min-w-0 text-left flex items-center gap-1"
        title={t('pageBuilder', 'editLinkUrl')}
      >
        <span className="text-xs truncate">{t('platforms', link.platform)}</span>
        <Pencil className="w-3 h-3 text-muted-foreground flex-shrink-0" />
      </button>
      <Switch
        checked={link.active}
        onCheckedChange={() => onToggle(link.id, link.active)}
      />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDelete(link.id)}
        className="h-8 w-8 text-muted-foreground hover:text-red-500"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function PageBuilder() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user } = useAuth();
  const isEditing = Boolean(pageId);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Generate random 5-character slug (a-z, A-Z, 0-9). Example: nONv8
  const generateRandomSlug = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  
  const [formData, setFormData] = useState({
    title: "",
    slug: generateRandomSlug(),
    custom_slug: "",
    artist_name: "",
    release_title: "",
    description: "",
    cover_image: "",
  });
  
  const [links, setLinks] = useState([]);
  const [newLink, setNewLink] = useState({ platform: "spotify", url: "" });
  const [qrEnabled, setQrEnabled] = useState(true);
  const [shareButtonsEnabled, setShareButtonsEnabled] = useState(true);
  const [pageTheme, setPageTheme] = useState("dark"); // Тема публичной страницы
  const [scanningSource, setScanningSource] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [autoSaving, setAutoSaving] = useState(false);
  const [pageCreated, setPageCreated] = useState(false); // Track if page was created
  const [createdPageId, setCreatedPageId] = useState(null); // Store created page ID for new pages
  const [resolvedPageId, setResolvedPageId] = useState(null); // Canonical page id (URL may be a slug)
  const [mobileTab, setMobileTab] = useState('settings'); // 'settings' or 'preview' - for mobile view
  const [showSectionMenu, setShowSectionMenu] = useState(false); // Mobile section dropdown
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [activeId, setActiveId] = useState(null); // Currently dragged link id (for DragOverlay)
  const [showDeletePageConfirm, setShowDeletePageConfirm] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  const qrRef = useRef(null);
  const formDataRef = useRef(formData);
  const pageThemeRef = useRef(pageTheme);
  const qrEnabledRef = useRef(qrEnabled);
  const shareButtonsEnabledRef = useRef(shareButtonsEnabled);
  const linksRef = useRef(links);
  const scanInputTimeoutRef = useRef(null); // Debounce timeout for URL input
  const saveTimeoutRef = useRef(null); // Debounce timeout for auto-save
  const autoSaveIntervalRef = useRef(null);
  const creatingPageRef = useRef(null); // In-flight create promise (prevents double-create)
  const pageCreatedRef = useRef(false); // Synchronous mirror of pageCreated state

  // Canonical page id for ALL backend calls. The URL param (pageId) may be a
  // human-readable slug, so we use the resolved id once the page is loaded,
  // falling back to the URL param (backend also accepts slug for safety).
  const apiPageId = resolvedPageId || createdPageId || pageId;

  const sanitizeSlug = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const hasMeaningfulPageData = (data = formDataRef.current, pageLinks = linksRef.current) => {
    return Boolean(
      data.artist_name?.trim() ||
      data.release_title?.trim() ||
      data.description?.trim() ||
      data.cover_image ||
      data.custom_slug?.trim() ||
      pageLinks.length > 0
    );
  };

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start (track active link for DragOverlay)
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handle drag end for reordering links
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id) {
      const oldIndex = links.findIndex(l => l.id === active.id);
      const newIndex = links.findIndex(l => l.id === over.id);
      
      const newLinks = arrayMove(links, oldIndex, newIndex);
      setLinks(newLinks);
      
      // Save new order to backend
      if (isEditing) {
        try {
          const linkIds = newLinks.map(l => l.id);
          await api.put(`/pages/${apiPageId}/links/reorder`, { link_ids: linkIds });
        } catch (error) {
          toast.error(t('errors', 'saveFailed'));
          // Revert on error
          setLinks(links);
        }
      }
    }
  };

  // Keep refs in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    pageThemeRef.current = pageTheme;
  }, [pageTheme]);
  
  useEffect(() => {
    qrEnabledRef.current = qrEnabled;
  }, [qrEnabled]);

  useEffect(() => {
    shareButtonsEnabledRef.current = shareButtonsEnabled;
  }, [shareButtonsEnabled]);

  useEffect(() => {
    linksRef.current = links;
  }, [links]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scanInputTimeoutRef.current) {
        clearTimeout(scanInputTimeoutRef.current);
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    autoSaveIntervalRef.current = setInterval(() => {
      instantSave();
    }, 10000);

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [pageId, pageCreated, createdPageId]);

  // Instant auto-save function
  const instantSave = async (overrideData = {}) => {
    // For new pages, create it first if not created yet
    if (!isEditing && !pageCreatedRef.current && !pageCreated) {
      if (!hasMeaningfulPageData({ ...formDataRef.current, ...overrideData })) {
        return null;
      }
      return createPageFirst(overrideData);
    }
    
    // Use resolved canonical id for editing, or createdPageId for newly created pages
    const targetPageId = apiPageId || createdPageId;
    if (!targetPageId) return;
    
    const currentFormData = { ...formDataRef.current, ...overrideData };
    
    setAutoSaving(true);
    try {
      let finalSlug = currentFormData.slug?.trim();
      if (!finalSlug) {
        finalSlug = generateRandomSlug();
      }
      
      let finalTitle = currentFormData.title?.trim();
      if (!finalTitle) {
        const parts = [currentFormData.artist_name, currentFormData.release_title].filter(Boolean);
        finalTitle = parts.length > 0 ? parts.join(" - ") : t('pageBuilder', 'newPage');
      }
      
      const pageData = { 
        ...currentFormData, 
        slug: finalSlug,
        title: finalTitle,
        qr_enabled: qrEnabledRef.current,
        share_buttons_enabled: shareButtonsEnabledRef.current,
        page_theme: pageThemeRef.current
      };
      
      await api.put(`/pages/${targetPageId}`, pageData);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  // Create page first for new pages (returns page ID, NO navigation - stays on same page)
  const createPageFirst = async (overrideData = {}, linksToAdd = []) => {
    // Already created? return existing id.
    if (pageCreatedRef.current || pageCreated) return createdPageId;
    // A create is already in flight — reuse the same promise so we never POST twice.
    if (creatingPageRef.current) return creatingPageRef.current;

    const promise = _createPageFirstImpl(overrideData, linksToAdd);
    creatingPageRef.current = promise;
    try {
      return await promise;
    } finally {
      creatingPageRef.current = null;
    }
  };

  const _createPageFirstImpl = async (overrideData = {}, linksToAdd = []) => {
    const currentFormData = { ...formDataRef.current, ...overrideData };
    if (!hasMeaningfulPageData(currentFormData, linksToAdd.length > 0 ? linksToAdd : linksRef.current)) {
      return null;
    }
    
    setAutoSaving(true);
    try {
      let finalSlug = currentFormData.slug?.trim();
      if (!finalSlug) {
        // Automatic slug: 5 chars (a-z, A-Z, 0-9), e.g. nONv8
        finalSlug = generateRandomSlug();
      }
      
      let finalTitle = currentFormData.title?.trim();
      if (!finalTitle) {
        const parts = [currentFormData.artist_name, currentFormData.release_title].filter(Boolean);
        finalTitle = parts.length > 0 ? parts.join(" - ") : t('pageBuilder', 'newPage');
      }
      
      const basePageData = { 
        ...currentFormData, 
        title: finalTitle,
        qr_enabled: qrEnabledRef.current,
        share_buttons_enabled: shareButtonsEnabledRef.current,
        page_theme: pageThemeRef.current
      };
      
      // Create page, retrying with a fresh random slug if the slug is taken
      let response = null;
      let attemptSlug = finalSlug;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          response = await api.post("/pages", { ...basePageData, slug: attemptSlug });
          finalSlug = attemptSlug;
          break;
        } catch (err) {
          if (err.response?.status === 400 && err.response?.data?.detail === "Slug already exists") {
            attemptSlug = generateRandomSlug();
            continue;
          }
          throw err;
        }
      }
      if (!response) {
        // Last resort: fully random slug
        attemptSlug = generateRandomSlug();
        response = await api.post("/pages", { ...basePageData, slug: attemptSlug });
        finalSlug = attemptSlug;
      }
      const newPageId = response.data.id;
      // Mark created synchronously so concurrent callers bail out immediately
      pageCreatedRef.current = true;
      
      // Add links to the created page
      const createdLinks = [];
      if (linksToAdd.length > 0) {
        for (const linkData of linksToAdd) {
          try {
            const linkResponse = await api.post(`/pages/${newPageId}/links`, {
              platform: linkData.platform,
              url: linkData.url,
              active: true
            });
            createdLinks.push(linkResponse.data);
          } catch (error) {
            console.error(`Failed to add ${linkData.platform} link to new page`);
          }
        }
      }
      
      // Update links state with server-created links (with real IDs)
      if (createdLinks.length > 0) {
        setLinks(createdLinks);
      }
      
      setPageCreated(true);
      setCreatedPageId(newPageId);
      setResolvedPageId(newPageId);
      
      // Update form data with final values
      setFormData(prev => ({
        ...prev,
        slug: finalSlug,
        title: finalTitle
      }));
      
      // Navigate to edit page to prevent duplicates (URL uses the slug).
      const urlSlug = response.data.custom_slug || response.data.slug || newPageId;
      navigate(`/multilinks/${urlSlug}/edit`, { replace: true });
      
      return newPageId;
    } catch (error) {
      console.error('Create page failed:', error);
      if (error.response?.data?.detail === "PAGE_LIMIT_REACHED") {
        toast.error(t('errors', 'pageLimitReached'));
      }
      return null;
    } finally {
      setAutoSaving(false);
    }
  };

  useEffect(() => {
    // Don't refetch right after we created the page ourselves (autofill flow):
    // the data is already in state and a refetch would wipe the just-added links.
    if (isEditing && !pageCreatedRef.current) {
      fetchPage();
    }
  }, [pageId]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pages/${pageId}`);
      setResolvedPageId(response.data.id);
      setFormData({
        title: response.data.title,
        slug: response.data.slug,
        custom_slug: response.data.custom_slug || "",
        artist_name: response.data.artist_name,
        release_title: response.data.release_title,
        description: response.data.description || "",
        cover_image: response.data.cover_image || "",
      });
      setLinks(response.data.links || []);
      setQrEnabled(response.data.qr_enabled !== false);
      setShareButtonsEnabled(response.data.share_buttons_enabled !== false);
      setPageTheme(response.data.page_theme || "dark");

      // Normalize URL to the human-readable slug (custom_slug preferred),
      // so the editor shows /multilinks/stayhigh/edit instead of a hash.
      const urlSlug = response.data.custom_slug || response.data.slug;
      if (urlSlug && urlSlug !== pageId) {
        navigate(`/multilinks/${urlSlug}/edit`, { replace: true });
      }
    } catch (error) {
      toast.error(t('errors', 'loadFailed'));
      navigate("/multilinks");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "custom_slug" ? sanitizeSlug(value) : value;
    setFormData(prev => {
      const updated = { ...prev, [name]: nextValue };
      
      // Auto-generate title only (NOT slug) from artist_name and release_title
      if (name === "artist_name" || name === "release_title") {
        const artist = name === "artist_name" ? nextValue : prev.artist_name;
        const release = name === "release_title" ? nextValue : prev.release_title;
        
        if (artist || release) {
          const title = [artist, release].filter(Boolean).join(" - ");
          updated.title = title;
          // Note: slug is NOT auto-generated anymore
        }
      }
      
      return updated;
    });
    
    if (name === "custom_slug") {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        instantSave({ custom_slug: nextValue });
      }, 2000);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const response = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const newCoverImage = response.data.cover_url;
      setFormData(prev => ({ ...prev, cover_image: newCoverImage }));
      toast.success(t('common', 'success'));
      
      // Instant save after upload
      if (isEditing) {
        setTimeout(() => instantSave({ cover_image: newCoverImage }), 300);
      }
    } catch (error) {
      toast.error(t('errors', 'uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const addLink = async (override) => {
    const platform = override?.platform || newLink.platform;
    const url = (override?.url ?? (newLink.url || "")).trim();
    if (!url) {
      toast.error(t('pageBuilder', 'enterUrl'));
      return;
    }
    // Avoid duplicates for the same platform+url
    if (links.some(l => l.platform === platform && l.url === url)) {
      setNewLink({ platform: "spotify", url: "" });
      return;
    }
    
    if (isEditing) {
      try {
        const response = await api.post(`/pages/${apiPageId}/links`, {
          platform,
          url,
          active: true
        });
        // Show the newly added link first
        const updated = [response.data, ...links];
        setLinks(updated);
        setNewLink({ platform: "spotify", url: "" });
        toast.success(t('pageBuilder', 'linkAdded'));
        // Persist the new order (new link first) and save
        try {
          await api.put(`/pages/${apiPageId}/links/reorder`, { link_ids: updated.map(l => l.id) });
        } catch (e) { /* non-fatal */ }
        instantSave();
      } catch (error) {
        toast.error(t('errors', 'generic'));
      }
    } else {
      // For new pages, create the page first and add the link
      const tempLink = { 
        id: Date.now().toString(),
        platform, 
        url,
        active: true,
        clicks: 0
      };
      setLinks(prev => [tempLink, ...prev]);
      setNewLink({ platform: "spotify", url: "" });
      
      // Create page and add link
      await createPageFirst({}, [{ platform, url }]);
    }
  };

  // Auto-add a typed/pasted link once it's a valid URL (debounced).
  const linkAutoAddTimeoutRef = useRef(null);
  const handleNewLinkUrlChange = (rawUrl) => {
    const url = rawUrl;
    const detectedPlatform = detectPlatformFromUrl(url);
    setNewLink(prev => ({ ...prev, url, platform: detectedPlatform || prev.platform }));

    if (linkAutoAddTimeoutRef.current) clearTimeout(linkAutoAddTimeoutRef.current);
    const trimmed = (url || "").trim();
    // Only auto-add complete-looking URLs
    if (/^https?:\/\/.+\..+/.test(trimmed)) {
      const platform = detectedPlatform || newLink.platform || "custom";
      linkAutoAddTimeoutRef.current = setTimeout(() => {
        addLink({ platform, url: trimmed });
      }, 700);
    }
  };

  const toggleLink = async (linkId, active) => {
    if (isEditing) {
      try {
        await api.put(`/pages/${apiPageId}/links/${linkId}`, { active: !active });
        setLinks(prev => prev.map(l => l.id === linkId ? { ...l, active: !active } : l));
      } catch (error) {
        toast.error(t('errors', 'generic'));
      }
    } else {
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, active: !active } : l));
    }
  };

  const deleteLink = async (linkId) => {
    if (isEditing) {
      try {
        await api.delete(`/pages/${apiPageId}/links/${linkId}`);
        setLinks(prev => prev.filter(l => l.id !== linkId));
        toast.success(t('pageBuilder', 'linkDeleted'));
      } catch (error) {
        toast.error(t('errors', 'deleteFailed'));
      }
    } else {
      setLinks(prev => prev.filter(l => l.id !== linkId));
    }
  };

  // Inline-edit a link's URL (click-to-edit)
  const updateLinkUrl = async (linkId, url) => {
    const targetId = apiPageId || createdPageId;
    if (isEditing || targetId) {
      try {
        await api.put(`/pages/${targetId}/links/${linkId}`, { url });
        setLinks(prev => prev.map(l => l.id === linkId ? { ...l, url } : l));
        toast.success(t('pageBuilder', 'autoSaved'));
      } catch (error) {
        toast.error(t('errors', 'generic'));
      }
    } else {
      setLinks(prev => prev.map(l => l.id === linkId ? { ...l, url } : l));
    }
  };

  // Delete the whole page (after confirm)
  const handleDeletePage = async () => {
    const targetId = apiPageId || createdPageId;
    if (!targetId) {
      navigate('/multilinks');
      return;
    }
    setDeletingPage(true);
    try {
      await api.delete(`/pages/${targetId}`);
      toast.success(t('pageBuilder', 'pageDeleted'));
      setShowDeletePageConfirm(false);
      navigate('/multilinks');
    } catch (error) {
      toast.error(t('errors', 'deleteFailed'));
      setDeletingPage(false);
    }
  };

  // Scan source to auto-detect platform links via Odesli API
  const scanSource = async (overrideInput) => {
    const rawInput = (typeof overrideInput === "string" ? overrideInput : scanInput);
    if (!rawInput.trim()) {
      toast.error(t('pageBuilder', 'autofillHint'));
      return;
    }

    setScanningSource(true);
    
    try {
      const sourceInput = rawInput.trim();
      
      // Check if input is a UPC code (numeric, 10-14 digits)
      const isUpcCode = /^\d{10,14}$/.test(sourceInput);
      
      const isValidUrl = isAutofillSource(sourceInput);
      
      if (!isValidUrl && !isUpcCode) {
        toast.error(t('pageBuilder', 'autofillHint'));
        setScanningSource(false);
        return;
      }

      // Call Odesli API via backend proxy
      toast.info(isUpcCode ? t('pageBuilder', 'searchingUPC') : t('pageBuilder', 'searchingLinks'));
      
      const odesliResponse = await api.get(`/lookup/odesli?url=${encodeURIComponent(sourceInput)}`);
      const odesliData = odesliResponse.data;
      const sourcePlatform = detectPlatformFromUrl(sourceInput);
      
      if (odesliData.error && Object.keys(odesliData.links || {}).length === 0 && !sourcePlatform) {
        toast.error(isUpcCode ? t('pageBuilder', 'upcNotFound') : t('pageBuilder', 'trackNotFound'));
        setScanningSource(false);
        return;
      }

      // Duplicate detector: warn if the user already has a page for this release
      if (odesliData.title) {
        try {
          const dupResp = await api.get(
            `/pages/check-duplicate?artist=${encodeURIComponent(odesliData.artistName || "")}&title=${encodeURIComponent(odesliData.title || "")}`
          );
          const dups = (dupResp.data?.duplicates || []).filter(d => d.id !== (apiPageId || createdPageId));
          if (dups.length > 0) {
            toast.warning(
              `${t('pageBuilder', 'duplicateWarning') || 'Похоже, у вас уже есть страница для этого релиза'}: ${dups[0].artist_name} — ${dups[0].release_title}`,
              { duration: 6000 }
            );
          }
        } catch (e) {
          // Non-fatal: duplicate check should never block autofill
        }
      }

      const platformLinks = odesliData.links || {};
      if (sourcePlatform && !platformLinks[sourcePlatform]) {
        platformLinks[sourcePlatform] = sourceInput;
      }
      const detectedLinks = [];
      let linksAdded = 0;

      // Update cover image if available
      let updatedFormData = { ...formData };
      if (odesliData.thumbnailUrl) {
        updatedFormData.cover_image = odesliData.thumbnailUrl;
        setFormData(prev => ({ ...prev, cover_image: odesliData.thumbnailUrl }));
      }

      // Update artist and release title if available and empty
      if (odesliData.artistName && !formData.artist_name) {
        updatedFormData.artist_name = odesliData.artistName;
        setFormData(prev => ({ ...prev, artist_name: odesliData.artistName }));
      }
      if (odesliData.title && !formData.release_title) {
        updatedFormData.release_title = odesliData.title;
        setFormData(prev => ({ ...prev, release_title: odesliData.title }));
      }
      
      // Auto-generate title only (NOT slug) if updated from Odesli
      if (updatedFormData.artist_name || updatedFormData.release_title) {
        const artist = updatedFormData.artist_name || '';
        const release = updatedFormData.release_title || '';
        if (artist || release) {
          const title = [artist, release].filter(Boolean).join(" - ");
          if (!formData.title) updatedFormData.title = title;
          // Note: slug is NOT auto-generated from Odesli data - user controls it
          setFormData(prev => ({ 
            ...prev, 
            title: prev.title || title
          }));
        }
      }

      // Add all platform links from Odesli
      for (const [platform, url] of Object.entries(platformLinks)) {
        const existing = links.find(l => l.platform === platform);
        if (!existing && url) {
          detectedLinks.push({ platform, url });
        }
      }

      if (detectedLinks.length === 0) {
        toast.info(t('pageBuilder', 'allLinksAdded'));
        setScanningSource(false);
        return;
      }

      // Sort detected links according to PLATFORM_ORDER
      detectedLinks.sort((a, b) => {
        const orderA = PLATFORM_ORDER.indexOf(a.platform);
        const orderB = PLATFORM_ORDER.indexOf(b.platform);
        const posA = orderA === -1 ? 999 : orderA;
        const posB = orderB === -1 ? 999 : orderB;
        return posA - posB;
      });

      // Collect all new links first
      const newLinks = [];
      
      // Add detected links
      for (const linkData of detectedLinks) {
        if (isEditing) {
          try {
            const response = await api.post(`/pages/${apiPageId}/links`, {
              platform: linkData.platform,
              url: linkData.url,
              active: true
            });
            newLinks.push(response.data);
            linksAdded++;
          } catch (error) {
            console.error(`Failed to add ${linkData.platform} link`);
          }
        } else {
          newLinks.push({
            id: Date.now().toString() + linkData.platform,
            platform: linkData.platform,
            url: linkData.url,
            active: true,
            clicks: 0
          });
          linksAdded++;
        }
      }

      // Combine existing links with new links and sort all by PLATFORM_ORDER
      const allLinks = [...links, ...newLinks];
      allLinks.sort((a, b) => {
        const orderA = PLATFORM_ORDER.indexOf(a.platform);
        const orderB = PLATFORM_ORDER.indexOf(b.platform);
        const posA = orderA === -1 ? 999 : orderA;
        const posB = orderB === -1 ? 999 : orderB;
        return posA - posB;
      });
      
      setLinks(allLinks);

      // If editing, update the order on backend
      if (isEditing && allLinks.length > 0) {
        try {
          const linkIds = allLinks.map(l => l.id);
          await api.put(`/pages/${apiPageId}/links/reorder`, { link_ids: linkIds });
        } catch (error) {
          console.error("Failed to reorder links");
        }
      }

      const platformNames = detectedLinks.slice(0, 5).map(l => 
        t('platforms', l.platform) || l.platform
      ).join(", ");
      
      const moreCount = detectedLinks.length > 5 ? ` +${detectedLinks.length - 5}` : "";
      toast.success(`${t('pageBuilder', 'linksAdded')}: ${linksAdded} (${platformNames}${moreCount})`);
      setScanInput("");
      
      // Wait 2 seconds for links to be fully added, then save
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Save after delay - NO navigation, stay on same page
      if (isEditing) {
        await instantSave(updatedFormData);
      } else if (pageCreatedRef.current || pageCreated) {
        // Page was already created (e.g. by autosave) while we were scanning —
        // attach the detected links to the existing page instead of recreating.
        const targetId = apiPageId || createdPageId;
        if (targetId) {
          const addedLinks = [];
          for (const linkData of detectedLinks) {
            try {
              const resp = await api.post(`/pages/${targetId}/links`, {
                platform: linkData.platform,
                url: linkData.url,
                active: true,
              });
              addedLinks.push(resp.data);
            } catch (e) {
              console.error(`Failed to add ${linkData.platform} link`);
            }
          }
          if (addedLinks.length > 0) {
            setLinks(prev => [...prev, ...addedLinks]);
          }
          await instantSave(updatedFormData);
        }
      } else {
        // For new pages, create page with all data AND links - NO redirect
        await createPageFirst(updatedFormData, detectedLinks);
      }
    } catch (error) {
      console.error("Scan error:", error);
      toast.error(t('pageBuilder', 'scanError'));
    } finally {
      setScanningSource(false);
    }
  };

  // Download QR code as PNG
  const downloadQRCode = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    canvas.width = 512;
    canvas.height = 512;
    
    img.onload = () => {
      ctx.fillStyle = "#18181b";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement("a");
      link.download = `${formData.slug || "qrcode"}-muslink.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
  };

  const getPublicUrl = () => {
    // Prefer the user-defined unique link (custom_slug) when present.
    const effectiveSlug = (formData.custom_slug && formData.custom_slug.trim())
      ? formData.custom_slug.trim()
      : formData.slug;
    return getMainDomainUrl(`/${effectiveSlug}`);
  };

  const getCustomPublicUrl = () => {
    return formData.custom_slug ? getMainDomainUrl(`/${formData.custom_slug}`) : "";
  };

  const getDisplayBaseUrl = () => {
    return getMainDomainUrl("/").replace(/^https?:\/\//, "").replace(/\/$/, "");
  };

  const copyFullUrl = async (type, url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(type);
      setTimeout(() => setCopiedSlug(null), 1200);
      instantSave();
    } catch (error) {
      console.error("Copy URL failed:", error);
    }
  };

  const getPlatformInfo = (platformId) => {
    const platform = PLATFORMS.find(p => p.id === platformId) || PLATFORMS[PLATFORMS.length - 1];
    return {
      ...platform,
      name: t('platforms', platform.id) || platform.id
    };
  };

  const renderUniqueLinkFields = (compact = false) => {
    const baseUrl = getDisplayBaseUrl();
    const primaryUrl = getPublicUrl();
    const customUrl = getCustomPublicUrl();

    return (
      <div className="space-y-3">
        <Label className={compact ? "text-sm" : "text-lg font-semibold"}>{t('pageBuilder', 'linkUrl')}</Label>
        <div className="rounded-xl border border-border bg-card/40 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <span className="text-sm font-medium text-foreground truncate">{baseUrl}/{formData.slug}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-9 w-9 rounded-full bg-muted hover:bg-muted/80 flex-shrink-0"
              onClick={() => copyFullUrl("primary", primaryUrl)}
              data-testid="copy-primary-url-btn"
            >
              {copiedSlug === "primary" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-2 px-4 py-3">
            <span className="text-sm text-muted-foreground flex-shrink-0">{baseUrl}/</span>
            <Input
              id="custom_slug"
              name="custom_slug"
              placeholder={t('pageBuilder', 'slugPlaceholder')}
              value={formData.custom_slug}
              onChange={handleChange}
              data-testid="custom-slug-input"
              className="h-9 min-w-[120px] flex-1 rounded-lg bg-background border-border"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-9 w-9 rounded-full bg-muted hover:bg-muted/80 flex-shrink-0"
              disabled={!formData.custom_slug}
              onClick={() => copyFullUrl("custom", customUrl)}
              data-testid="copy-custom-url-btn"
            >
              {copiedSlug === "custom" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background overflow-hidden flex flex-col">
      {/* Desktop Layout */}
      <div className="hidden lg:flex flex-1 min-h-0 overflow-hidden">
        <aside className="w-64 flex-shrink-0 border-r border-border bg-background p-6">
          <div className="mb-10 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/multilinks")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
              aria-label={t('common', 'back') || 'Back'}
              data-testid="builder-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <MuslinkLogo height={28} theme={theme} />
          </div>
          <div className="space-y-3">
            <button className="w-full flex items-center justify-between text-sm font-medium text-muted-foreground">
              <span>{t('pageBuilder', 'sidebarBasic')}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            <button className="w-full flex items-center justify-between rounded-xl bg-primary/10 px-4 py-3 text-sm font-medium leading-5 text-primary">
              <span>{t('pageBuilder', 'sidebarBasicSettings')}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            </button>
          </div>
        </aside>

        <div className="flex-1 min-w-0 h-full flex overflow-hidden">
          {/* Left Side - Editor */}
          <div className="w-2/5 min-w-0 overflow-y-auto scrollbar-none px-8 py-6 border-r border-border">
            <div className="max-w-3xl mx-auto space-y-6 pb-10">
          
          {/* 1. Автозаполнение через Odesli - ПЕРВЫМ */}
          <section className="overflow-hidden">
            <div className="p-3 sm:p-4 rounded-xl bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <Search className="w-4 h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium">{t('pageBuilder', 'autofill')}</span>
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-2 sm:mb-3">
                {t('pageBuilder', 'autofillDesc')}
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder={t('pageBuilder', 'autofillPlaceholder')}
                  value={scanInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setScanInput(value);
                    
                    // Clear previous auto-trigger timeout
                    if (scanInputTimeoutRef.current) {
                      clearTimeout(scanInputTimeoutRef.current);
                    }
                    
                    // Auto-start search instantly once a valid source is entered/pasted
                    if (isAutofillSource(value)) {
                      scanInputTimeoutRef.current = setTimeout(() => {
                        scanSource(value);
                      }, 300);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !scanningSource && scanInput.trim()) {
                      e.preventDefault();
                      if (scanInputTimeoutRef.current) clearTimeout(scanInputTimeoutRef.current);
                      scanSource();
                    }
                  }}
                  data-testid="scan-source-input"
                  className="h-9 sm:h-10 bg-muted border-zinc-700 flex-1 text-xs sm:text-sm"
                />
                <Button 
                  onClick={() => scanSource()}
                  disabled={scanningSource || !scanInput.trim()}
                  className="h-9 sm:h-10 bg-primary hover:bg-primary/90 px-3"
                  data-testid="scan-source-btn"
                >
                  {scanningSource ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </section>
          
          {/* 4. Ссылки на платформы */}
          <section className="overflow-hidden">
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">{t('pageBuilder', 'platformLinks')}</h2>
            
            {/* Add New Link — just paste, platform is auto-detected and added automatically */}
            <div className="mb-3 sm:mb-4">
              <Input
                placeholder={t('pageBuilder', 'pasteLinkPlaceholder') || 'Просто вставьте ссылку сюда'}
                value={newLink.url}
                onChange={(e) => handleNewLinkUrlChange(e.target.value)}
                data-testid="link-url-input"
                className="h-10 sm:h-12 bg-card border-zinc-800 w-full"
              />
            </div>
            
            {/* Links List with Drag and Drop */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={links.map(l => l.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 w-full page-builder-links">
                  {links.map((link) => {
                    const platform = getPlatformInfo(link.platform);
                    return (
                      <SortableLinkItem
                        key={link.id}
                        link={link}
                        platform={platform}
                        onToggle={toggleLink}
                        onDelete={deleteLink}
                        onUpdateUrl={updateLinkUrl}
                        t={t}
                      />
                    );
                  })}
                  
                  {links.length === 0 && (
                    <p className="text-center text-muted-foreground py-8 border border-dashed border-zinc-800 rounded-xl">
                      {t('pageBuilder', 'noLinks')}
                    </p>
                  )}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeId ? (() => {
                  const dl = links.find(l => l.id === activeId);
                  if (!dl) return null;
                  const dp = getPlatformInfo(dl.platform);
                  const DIcon = dp.icon;
                  return (
                    <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-card border border-primary shadow-2xl shadow-primary/30 ring-2 ring-primary cursor-grabbing scale-[1.03]">
                      <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: dp.color }}>
                        <DIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <p className="font-medium text-sm sm:text-base">{dp.name}</p>
                    </div>
                  );
                })() : null}
              </DragOverlay>
            </DndContext>
          </section>
          
          {/* 3. Обложка */}
          <section>
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">{t('pageBuilder', 'coverImage')}</h2>
            <div className="space-y-3 sm:space-y-4">
              <div 
                className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-muted overflow-hidden border-2 border-dashed border-zinc-700 hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => document.getElementById('cover-upload').click()}
              >
                {formData.cover_image ? (
                  <img 
                    src={formData.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.cover_image}` : formData.cover_image}
                    alt="Cover"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">{t('common', 'upload')}</span>
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-primary"></div>
                  </div>
                )}
              </div>
              <input
                id="cover-upload"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
                data-testid="cover-upload-input"
              />
              <p className="text-xs text-muted-foreground">
                {t('pageBuilder', 'coverHint')}
              </p>
            </div>
          </section>
          
          {/* 2. Основная информация */}
          <section>
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">{t('pageBuilder', 'basicInfo')}</h2>
            <div className="space-y-3 sm:space-y-4">
              {/* Имя артиста */}
              <div className="space-y-2">
                <Label htmlFor="artist_name" className="text-sm">{t('pageBuilder', 'artistName')}</Label>
                <Input
                  id="artist_name"
                  name="artist_name"
                  placeholder={t('pageBuilder', 'artistPlaceholder')}
                  value={formData.artist_name}
                  onChange={handleChange}
                  required
                  data-testid="artist-name-input"
                  className="h-10 sm:h-12 bg-card border-zinc-800"
                />
              </div>
              
              {/* Название релиза */}
              <div className="space-y-2">
                <Label htmlFor="release_title" className="text-sm">{t('pageBuilder', 'releaseTitle')}</Label>
                <Input
                  id="release_title"
                  name="release_title"
                  placeholder={t('pageBuilder', 'releasePlaceholder')}
                  value={formData.release_title}
                  onChange={handleChange}
                  required
                  data-testid="release-title-input"
                  className="h-10 sm:h-12 bg-card border-zinc-800"
                />
              </div>
              
              {/* Описание */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm">{t('pageBuilder', 'description')}</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder={t('pageBuilder', 'descPlaceholder')}
                  value={formData.description}
                  onChange={handleChange}
                  data-testid="description-input"
                  className="bg-card border-zinc-800 min-h-[80px] sm:min-h-[100px]"
                />
              </div>

              {renderUniqueLinkFields()}
            </div>
          </section>
          

          
          {/* 3.5. Вид дизайна страницы */}
          <section>
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">{t('pageBuilder', 'pageDesign')}</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { 
                  setPageTheme("dark"); 
                  if (isEditing) setTimeout(() => instantSave({ page_theme: "dark" }), 100);
                }}
                className={`p-4 rounded-xl border-2 transition-all ${
                  pageTheme === "dark" 
                    ? "border-primary bg-primary/10" 
                    : "border-border bg-card hover:border-primary/50"
                }`}
                data-testid="theme-dark-btn"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-xl bg-zinc-900 flex items-center justify-center">
                    <Moon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-medium">{t('pageBuilder', 'darkTheme')}</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => { 
                  setPageTheme("light"); 
                  if (isEditing) setTimeout(() => instantSave({ page_theme: "light" }), 100);
                }}
                className={`p-4 rounded-xl border-2 transition-all ${
                  pageTheme === "light" 
                    ? "border-primary bg-primary/10" 
                    : "border-border bg-card hover:border-primary/50"
                }`}
                data-testid="theme-light-btn"
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Sun className="w-6 h-6 text-yellow-500" />
                  </div>
                  <span className="text-sm font-medium">{t('pageBuilder', 'lightTheme')}</span>
                </div>
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('pageBuilder', 'pageDesignHint')}
            </p>
          </section>
          

          {/* Share Buttons Section */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">{t('pageBuilder', 'shareButtons')}</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {shareButtonsEnabled ? t('common', 'enabled') : t('common', 'disabled')}
                </span>
                <Switch
                  checked={shareButtonsEnabled}
                  onCheckedChange={(checked) => {
                    setShareButtonsEnabled(checked);
                    if (isEditing) setTimeout(() => instantSave({ share_buttons_enabled: checked }), 100);
                  }}
                  data-testid="share-buttons-toggle"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('pageBuilder', 'shareButtonsHint')}
            </p>
          </section>

          
          {/* QR Code Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">{t('pageBuilder', 'qrCode')}</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {qrEnabled ? t('common', 'enabled') : t('common', 'disabled')}
                </span>
                <Switch
                  checked={qrEnabled}
                  onCheckedChange={(checked) => { 
                    setQrEnabled(checked); 
                    if (isEditing) setTimeout(() => instantSave({ qr_enabled: checked }), 100);
                  }}
                  data-testid="qr-toggle"
                />
              </div>
            </div>
            
            {qrEnabled && formData.slug && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 sm:p-6 rounded-xl panel-card"
              >
                <div className="flex flex-col items-center gap-4">
                  <div 
                    ref={qrRef}
                    className="p-3 sm:p-4 bg-white rounded-xl"
                  >
                    <QRCodeSVG
                      value={getPublicUrl()}
                      size={140}
                      level="H"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#18181b"
                    />
                  </div>
                  <div className="text-center w-full">
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('pageBuilder', 'qrDesc')}
                    </p>
                    <p className="text-xs text-zinc-500 mb-4 font-mono break-all px-2" style={{ wordBreak: 'break-word' }}>
                      {getPublicUrl()}
                    </p>
                    <Button
                      onClick={downloadQRCode}
                      variant="outline"
                      className="rounded-full"
                      data-testid="download-qr-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('pageBuilder', 'downloadPng')}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
            
            {qrEnabled && !formData.slug && (
              <p className="text-center text-muted-foreground py-6 border border-dashed border-zinc-800 rounded-xl">
                {t('pageBuilder', 'enterUrlForQr')}
              </p>
            )}
            
            {!qrEnabled && (
              <p className="text-center text-muted-foreground py-6 border border-dashed border-zinc-800 rounded-xl">
                {t('pageBuilder', 'qrDisabled')}
              </p>
            )}
          </section>

          {/* Delete page (desktop) */}
          {(isEditing || createdPageId) && (
            <section className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeletePageConfirm(true)}
                className="w-full rounded-xl border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                data-testid="delete-page-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('pageBuilder', 'deletePage')}
              </Button>
            </section>
          )}
          
            </div>
          </div>
        
        {/* Right Side - Preview (Desktop) */}
          <div className="w-3/5 min-w-0 h-full bg-zinc-950 overflow-y-auto overflow-x-hidden relative">
            <a
              href={getPublicUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-6 right-6 z-10"
              data-testid="preview-open-page-link"
            >
              <Button type="button" variant="outline" className="rounded-full bg-zinc-950/80 border-white/10 text-white hover:bg-white/10">
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('pageBuilder', 'viewPage')}
              </Button>
            </a>
            <div className="flex justify-center">
              <PagePreview
                formData={{ ...formData, page_theme: pageTheme }}
                links={links}
                qrEnabled={qrEnabled}
                shareButtonsEnabled={shareButtonsEnabled}
                publicUrl={getPublicUrl()}
                isVerified={user?.is_verified}
                compact
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Layout */}
      <div className="lg:hidden">
        {/* Mobile Tab Switcher (Настройки / Превью) */}
        <div className="flex items-center gap-2 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate('/multilinks')}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-muted text-foreground flex-shrink-0"
            aria-label={t('common', 'back') || 'Back'}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex flex-1 items-center rounded-full bg-muted p-1">
            <button
              type="button"
              onClick={() => setMobileTab('settings')}
              className={`flex-1 h-9 rounded-full text-sm font-medium transition-colors ${mobileTab === 'settings' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
              data-testid="mobile-tab-settings"
            >
              {t('pageBuilder', 'tabSettings')}
            </button>
            <button
              type="button"
              onClick={() => setMobileTab('preview')}
              className={`flex-1 h-9 rounded-full text-sm font-medium transition-colors ${mobileTab === 'preview' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
              data-testid="mobile-tab-preview"
            >
              {t('pageBuilder', 'tabPreview')}
            </button>
          </div>
        </div>

        {/* Mobile Settings Tab */}
        {mobileTab === 'settings' && (
          <div className="flex flex-col h-[calc(100vh-120px)]">
            {/* Mobile Section Dropdown */}
            <div className="px-4 py-3 border-b border-border">
              <button
                onClick={() => setShowSectionMenu(!showSectionMenu)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-muted"
              >
                <span className="text-sm font-medium">{t('pageBuilder', 'sidebarBasicSettings')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showSectionMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {showSectionMenu && (
                <div className="mt-2 rounded-xl bg-card border border-border overflow-hidden">
                  <button className="w-full flex items-center gap-3 px-4 py-3 text-sm bg-primary/10 text-primary font-medium">
                    <span className="w-2 h-2 rounded-full bg-primary" />
                    {t('pageBuilder', 'sidebarBasicSettings')}
                  </button>
                </div>
              )}
            </div>
            
            {/* Mobile Form Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-6">
                {/* 1. Автозаполнение через Odesli */}
                <section className="overflow-hidden">
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Search className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">{t('pageBuilder', 'autofill')}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      {t('pageBuilder', 'autofillDesc')}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder={t('pageBuilder', 'autofillPlaceholder')}
                        value={scanInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setScanInput(value);
                          if (scanInputTimeoutRef.current) {
                            clearTimeout(scanInputTimeoutRef.current);
                          }
                          if (isAutofillSource(value)) {
                            scanInputTimeoutRef.current = setTimeout(() => {
                              scanSource(value);
                            }, 300);
                          }
                        }}
                        className="flex-1 h-10 bg-card border-zinc-800"
                        data-testid="mobile-autofill-input"
                      />
                      <Button 
                        onClick={() => scanSource()}
                        disabled={scanningSource || !scanInput.trim()}
                        className="bg-primary hover:bg-primary/90 h-10 px-4"
                      >
                        {scanningSource ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </section>
          
          {/* 4. Platform Links */}
                <section>
                  <h3 className="text-sm font-medium mb-3">{t('pageBuilder', 'platformLinks')}</h3>
                  
                  {/* Existing Links with Drag and Drop */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={links.map(l => l.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2 mb-4">
                        {links.map((link) => {
                          const platform = getPlatformInfo(link.platform);
                          return (
                            <MobileSortableLinkItem
                              key={link.id}
                              link={link}
                              platform={platform}
                              onToggle={toggleLink}
                              onDelete={deleteLink}
                              onUpdateUrl={updateLinkUrl}
                              t={t}
                            />
                          );
                        })}
                        
                        {links.length === 0 && (
                          <div className="text-center py-6 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
                            {t('pageBuilder', 'noLinks')}
                          </div>
                        )}
                      </div>
                    </SortableContext>
                    <DragOverlay>
                      {activeId ? (() => {
                        const dl = links.find(l => l.id === activeId);
                        if (!dl) return null;
                        const dp = getPlatformInfo(dl.platform);
                        const DIcon = dp.icon;
                        return (
                          <div className="flex items-center gap-2 p-3 rounded-xl bg-card border border-primary shadow-2xl shadow-primary/30 ring-2 ring-primary cursor-grabbing scale-[1.03]">
                            <GripVertical className="w-4 h-4 text-primary flex-shrink-0" />
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: dp.color + '20' }}>
                              {dp.isImage ? <DIcon className="w-5 h-5" /> : <DIcon className="w-5 h-5" style={{ color: dp.color }} />}
                            </div>
                            <span className="text-xs truncate flex-1">{t('platforms', dl.platform)}</span>
                          </div>
                        );
                      })() : null}
                    </DragOverlay>
                  </DndContext>
                  
                  {/* Add New Link — paste only, platform auto-detected and added automatically */}
                  <div>
                    <Input
                      placeholder={t('pageBuilder', 'pasteLinkPlaceholder') || 'Просто вставьте ссылку сюда'}
                      value={newLink.url}
                      onChange={(e) => handleNewLinkUrlChange(e.target.value)}
                      className="w-full h-10 bg-card border-border"
                      data-testid="mobile-link-url-input"
                    />
                  </div>
                </section>
          
          {/* 3. Cover Image */}
                <section>
                  <h3 className="text-sm font-medium mb-3">{t('pageBuilder', 'coverImage')}</h3>
                  <div 
                    className="relative w-32 h-32 rounded-2xl bg-muted overflow-hidden border-2 border-dashed border-zinc-700 hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('mobile-cover-upload').click()}
                  >
                    {formData.cover_image ? (
                      <img 
                        src={formData.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.cover_image}` : formData.cover_image}
                        alt="Cover"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                        <span className="text-xs text-muted-foreground">{t('common', 'upload')}</span>
                      </div>
                    )}
                    {uploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-primary"></div>
                      </div>
                    )}
                  </div>
                  <input
                    id="mobile-cover-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="mobile-cover-upload-input"
                  />
                </section>

                {/* 2. Basic Info */}
                <section>
                  <h3 className="text-sm font-medium mb-3">{t('pageBuilder', 'basicInfo')}</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="mobile_artist_name" className="text-xs text-muted-foreground">{t('pageBuilder', 'artistLabel')}</Label>
                      <Input
                        id="mobile_artist_name"
                        name="artist_name"
                        placeholder={t('pageBuilder', 'artistPlaceholder')}
                        value={formData.artist_name}
                        onChange={handleChange}
                        required
                        className="mt-1 h-10 bg-card border-zinc-800"
                        data-testid="mobile-artist-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile_release_title" className="text-xs text-muted-foreground">{t('pageBuilder', 'releaseLabel')}</Label>
                      <Input
                        id="mobile_release_title"
                        name="release_title"
                        placeholder={t('pageBuilder', 'releasePlaceholder')}
                        value={formData.release_title}
                        onChange={handleChange}
                        required
                        className="mt-1 h-10 bg-card border-zinc-800"
                        data-testid="mobile-release-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile_description" className="text-xs text-muted-foreground">{t('pageBuilder', 'descriptionLabel')}</Label>
                      <textarea
                        id="mobile_description"
                        name="description"
                        placeholder={t('pageBuilder', 'descriptionPlaceholder')}
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className="mt-1 w-full rounded-xl border border-zinc-800 bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="mobile-description-input"
                      />
                    </div>
                    {renderUniqueLinkFields(true)}
                  </div>
                </section>





                {/* 5. Page Design */}
                <section>
                  <h3 className="text-sm font-medium mb-3">{t('pageBuilder', 'pageDesign')}</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { setPageTheme("dark"); if (isEditing) setTimeout(() => instantSave({ page_theme: "dark" }), 100); }}
                      className={`p-4 rounded-xl border-2 transition-all ${pageTheme === "dark" ? "border-primary bg-primary/10" : "border-border bg-card"}`}
                    >
                      <Moon className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-xs">{t('pageBuilder', 'darkTheme')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPageTheme("light"); if (isEditing) setTimeout(() => instantSave({ page_theme: "light" }), 100); }}
                      className={`p-4 rounded-xl border-2 transition-all ${pageTheme === "light" ? "border-primary bg-primary/10" : "border-border bg-card"}`}
                    >
                      <Sun className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
                      <span className="text-xs">{t('pageBuilder', 'lightTheme')}</span>
                    </button>
                  </div>
                </section>

                {/* 6. Share Buttons */}
                <section>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Copy className="w-4 h-4 text-primary" />
                      {t('pageBuilder', 'shareButtons')}
                    </h3>
                    <Switch
                      checked={shareButtonsEnabled}
                      onCheckedChange={(checked) => {
                        setShareButtonsEnabled(checked);
                        if (isEditing) setTimeout(() => instantSave({ share_buttons_enabled: checked }), 100);
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('pageBuilder', 'shareButtonsHint')}
                  </p>
                </section>

                {/* 7. QR Code */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <QrCode className="w-4 h-4 text-primary" />
                      {t('pageBuilder', 'qrCode')}
                    </h3>
                    <Switch
                      checked={qrEnabled}
                      onCheckedChange={(checked) => { setQrEnabled(checked); if (isEditing) setTimeout(() => instantSave({ qr_enabled: checked }), 100); }}
                    />
                  </div>
                  
                  {qrEnabled && formData.slug && (
                    <div className="p-4 rounded-xl bg-muted/50 border border-border flex flex-col items-center">
                      <div ref={qrRef} className="p-3 bg-white rounded-xl mb-3">
                        <QRCodeSVG
                          value={getPublicUrl()}
                          size={100}
                          level="H"
                          includeMargin={false}
                          bgColor="#ffffff"
                          fgColor="#18181b"
                        />
                      </div>
                      <Button
                        onClick={downloadQRCode}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t('pageBuilder', 'downloadPng')}
                      </Button>
                    </div>
                  )}
                </section>

                {/* Delete page (mobile) */}
                {(isEditing || createdPageId) && (
                  <section className="pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDeletePageConfirm(true)}
                      className="w-full rounded-xl border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('pageBuilder', 'deletePage')}
                    </Button>
                  </section>
                )}

              </div>
            </div>
          </div>
        )}
        
        {/* Mobile Preview Tab */}
        {mobileTab === 'preview' && (
          <div className="h-[calc(100vh-120px)] bg-zinc-950 overflow-auto">
            <PagePreview
              formData={{ ...formData, page_theme: pageTheme }}
              links={links}
              qrEnabled={qrEnabled}
              shareButtonsEnabled={shareButtonsEnabled}
              publicUrl={getPublicUrl()}
              isVerified={user?.is_verified}
            />
          </div>
        )}
      </div>
      
      <ConfirmDialog
        open={showDeletePageConfirm}
        title={t('pageBuilder', 'deletePage')}
        description={t('pageBuilder', 'deletePageConfirm')}
        confirmText={t('common', 'delete')}
        cancelText={t('common', 'cancel')}
        destructive
        loading={deletingPage}
        onConfirm={handleDeletePage}
        onCancel={() => setShowDeletePageConfirm(false)}
      />
    </div>
  );
}
